<template>
  <div>
    <h1>Payments awaiting processing</h1>
    <v-btn @click="update" small> <v-icon>refresh</v-icon>Refresh </v-btn>
    <span v-if="lastUpdated"
      >Last updated: {{ lastUpdated | formatDateTimeSeconds }}</span
    >
    <v-alert :value="true" type="info"
      >Below are listed payment requests which have been authorised for
      processing</v-alert
    >
    <v-data-table
      :headers="computedHeadersRequestsAuthorised"
      :items="computedRequestsAuthorised"
      hide-actions
      class="elevation-1"
      :pagination.sync="paginationRequestsAuthorised"
      v-if="computedRequestsAuthorised"
    >
      <template v-slot:items="props">
        <tr>
          <td>{{ props.item.id }}</td>
          <td>{{ props.item.created | formatDateTimeSeconds }}</td>
          <td>
            <span v-if="props.item.file">
              <FileZ :file="props.item.file" />
            </span>
            <span v-if="!props.item.file">{{ props.item.fileid }}</span>
          </td>
          <td>{{ props.item.amount | toCurrency }}</td>
          <td>{{ props.item.issuer }}</td>
          <td>
            <span v-if="props.item.purpose">{{ props.item.purpose.body }}</span>
            <span v-if="!props.item.purpose">{{ props.item.purposeid }}</span>
          </td>
          <td>{{ props.item.authoriseCreated | formatDateTimeSeconds }}</td>
          <td v-if="$keycloak.hasRealmRole('paymentadmin')">
            <v-btn
              :to="{ name: 'process', params: { id: props.item.id } }"
              small
              color="info"
            >
              Process
              <v-icon>chevron_right</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-alert :value="true" type="info"
      >Below are listed payment requests which have been processed in last 4
      days</v-alert
    >
    <v-data-table
      :headers="headersRequestsTaken"
      :items="computedRequestsTaken"
      hide-actions
      class="elevation-1"
      :pagination.sync="paginationRequestsTaken"
      v-if="computedRequestsTaken"
    >
      <template v-slot:items="props">
        <tr>
          <td>{{ props.item.id }}</td>
          <td>{{ props.item.created | formatDateTimeSeconds }}</td>
          <td>
            <span v-if="props.item.file">
              <FileZ :file="props.item.file" />
            </span>
            <span v-if="!props.item.file">{{ props.item.fileid }}</span>
          </td>
          <td>{{ props.item.amount | toCurrency }}</td>
          <td>{{ props.item.issuer }}</td>
          <td>
            <span v-if="props.item.purpose">{{ props.item.purpose.body }}</span>
            <span v-if="!props.item.purpose">{{ props.item.purposeid }}</span>
          </td>
          <td>{{ props.item.authoriseArchived | formatDateTimeSeconds }}</td>
          <td>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="download(props.item)"
                  color="warning"
                  v-on="on"
                  small
                >
                  <v-icon>picture_as_pdf</v-icon>
                </v-btn>
              </template>
              <span>Receipt {{ props.item.id }}</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-alert :value="true" type="info"
      >Below are listed payment requests awaiting authorisation</v-alert
    >
    <v-data-table
      :headers="computedHeadersRequestsUnauthorised"
      :items="computedRequestsUnauthorised"
      hide-actions
      class="elevation-1"
      :pagination.sync="paginationRequestsUnauthorised"
      v-if="computedRequestsUnauthorised"
    >
      <template v-slot:items="props">
        <tr>
          <td v-if="$keycloak.hasRealmRole('root')">
            <router-link
              :to="{ name: 'request', params: { id: props.item.id } }"
              >Authorise</router-link
            >
          </td>
          <td>{{ props.item.id }}</td>
          <td>{{ props.item.created | formatDateTimeSeconds }}</td>
          <td>
            <span v-if="props.item.file">
              <FileZ :file="props.item.file" />
            </span>
            <span v-if="!props.item.file">{{ props.item.fileid }}</span>
          </td>
          <td>{{ props.item.amount | toCurrency }}</td>
          <td>
            <span v-if="props.item.purpose">{{ props.item.purpose.body }}</span>
            <span v-if="!props.item.purpose">{{ props.item.purposeid }}</span>
          </td>
          <td v-if="$keycloak.hasRealmRole('paymentadmin')">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="dismiss(props.item)"
                  color="error"
                  v-on="on"
                  small
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Dismiss {{ props.item.id }}</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import nbmlaw from "@/api/nbmlaw";
import FileZ from "@/components/link/FileZ";
import { convertListOfObjectsToMap } from "@/util/helpers";
export default {
  name: "Authorise",
  metaInfo() {
    return {
      title: "Card payments",
      titleTemplate: "%s - Payments - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true,
      },
    };
  },
  components: { FileZ },
  computed: {
    computedRequestsAuthorised() {
      if (!this.requestsAuthorised) {
        return undefined;
      }
      return this.requestsAuthorised.map((r) => {
        const purpose =
          this.purposes && this.purposes[r.purposeid]
            ? this.purposes[r.purposeid]
            : undefined;
        const file = this.requestsAuthorisedFiles
          ? this.requestsAuthorisedFiles[r.fileid]
          : undefined;
        const authorise = this.requestsAuthorisedAuthorises
          ? this.requestsAuthorisedAuthorises[r.id]
          : undefined;
        const authoriseCreated = authorise ? authorise.created : undefined;
        const issuer = this.requestsAuthorisedIssuers
          ? this.requestsAuthorisedIssuers[r.id].name
          : undefined;
        return {
          ...r,
          purpose,
          file,
          authorise,
          authoriseCreated,
          issuer,
        };
      });
    },
    computedRequestsTaken() {
      if (!this.requestsTaken) {
        return undefined;
      }
      return this.requestsTaken.map((r) => {
        const purpose =
          this.purposes && this.purposes[r.purposeid]
            ? this.purposes[r.purposeid]
            : undefined;
        const file = this.requestsTakenFiles
          ? this.requestsTakenFiles[r.fileid]
          : undefined;
        const authorise = this.requestsTakenAuthorises
          ? this.requestsTakenAuthorises[r.id]
          : undefined;
        const authoriseArchived = authorise ? authorise.archived : undefined;
        const issuer = this.requestsTakenIssuers
          ? this.requestsTakenIssuers[r.id].name
          : undefined;
        return {
          ...r,
          purpose,
          file,
          authorise,
          authoriseArchived,
          issuer,
        };
      });
    },
    computedRequestsUnauthorised() {
      if (!this.requestsUnauthorised) {
        return undefined;
      }
      return this.requestsUnauthorised.map((r) => {
        const purpose =
          this.purposes && this.purposes[r.purposeid]
            ? this.purposes[r.purposeid]
            : undefined;
        const file = this.requestsUnauthorisedFiles
          ? this.requestsUnauthorisedFiles[r.fileid]
          : undefined;
        return {
          ...r,
          purpose,
          file,
        };
      });
    },
    computedHeadersRequestsAuthorised() {
      let headers = [
        {
          text: "Id",
          value: "id",
        },
        {
          text: "Requested",
          value: "created",
        },
        {
          text: "File reference",
          value: "file.id",
        },
        {
          text: "Amount",
          value: "amount",
        },
        {
          text: "Issuer",
          value: "issuer",
        },
        {
          text: "Purpose",
          value: "purpose.body",
        },
        {
          text: "Authorised",
          value: "authoriseCreated",
        },
      ];
      if (this.$keycloak.hasRealmRole("paymentadmin")) {
        headers.push({
          text: " ",
          sortable: false,
        });
      }
      return headers;
    },
    computedHeadersRequestsUnauthorised() {
      let headers = [
        {
          text: "Id",
          value: "id",
        },
        {
          text: "Requested",
          value: "created",
        },
        {
          text: "File reference",
          value: "file.id",
        },
        {
          text: "Amount",
          value: "amount",
        },
        {
          text: "Issuer",
          value: "issuer",
        },
        {
          text: "Purpose",
          value: "purpose.body",
        },
      ];
      if (this.$keycloak.hasRealmRole("root")) {
        headers.unshift({
          text: "34",
          sortable: false,
        });
      }
      if (this.$keycloak.hasRealmRole("paymentadmin")) {
        headers.push({
          text: " ",
          sortable: false,
        });
      }
      return headers;
    },
  },
  data() {
    return {
      requestsAuthorised: null,
      requestsAuthorisedFiles: null,
      requestsAuthorisedAuthorises: null,
      requestsAuthorisedIssuers: null,
      requestsTaken: null,
      requestsTakenFiles: null,
      requestsTakenAuthorises: null,
      requestsTakenIssuers: null,
      requestsUnauthorised: null,
      requestsUnauthorisedFiles: null,
      purposes: null,
      lastUpdated: null,
      paginationRequestsAuthorised: {
        sortBy: "body",
        descending: false,
        page: 1,
        rowsPerPage: -1,
      },
      headersRequestsTaken: [
        {
          text: "Id",
          value: "id",
        },
        {
          text: "Requested",
          value: "created",
        },
        {
          text: "File reference",
          value: "file.id",
        },
        {
          text: "Amount",
          value: "amount",
        },
        {
          text: "Issuer",
          value: "issuer",
        },
        {
          text: "Purpose",
          value: "purpose.body",
        },
        {
          text: "Processed",
          value: "authoriseArchived",
        },
        { text: " ", sortable: false },
      ],
      paginationRequestsTaken: {
        sortBy: "authoriseArchived",
        descending: true,
        page: 1,
        rowsPerPage: -1,
      },
      paginationRequestsUnauthorised: {
        sortBy: "created",
        descending: true,
        page: 1,
        rowsPerPage: -1,
      },
    };
  },
  methods: {
    async fetchRequestsAuthorised() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/authorised`
      );
      // console.log(response.data);
      this.requestsAuthorised = response.data;
    },
    async fetchRequestsAuthorisedFiles() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/authorised/files`
      );
      // console.log(response.data);
      this.requestsAuthorisedFiles = response.data;
    },
    async fetchRequestsAuthorisedAuthorises() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/authorised/authorises`
      );
      // console.log(response.data);
      this.requestsAuthorisedAuthorises = response.data;
    },
    async fetchRequestsAuthorisedIssuers() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/authorised/issuers`
      );
      // console.log(response.data);
      this.requestsAuthorisedIssuers = response.data;
    },
    async fetchRequestsTaken() {
      const response = await nbmlaw.get(`/cors/admin/payment/requests/taken`);
      // console.log(response.data);
      this.requestsTaken = response.data;
    },
    async fetchRequestsTakenFiles() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/taken/files`
      );
      // console.log(response.data);
      this.requestsTakenFiles = response.data;
    },
    async fetchRequestsTakenAuthorises() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/taken/authorises`
      );
      // console.log(response.data);
      this.requestsTakenAuthorises = response.data;
    },
    async fetchRequestsTakenIssuers() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/taken/issuers`
      );
      // console.log(response.data);
      this.requestsTakenIssuers = response.data;
    },
    async fetchRequestsUnauthorised() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/unauthorised`
      );
      // console.log(response.data);
      this.requestsUnauthorised = response.data;
    },
    async fetchRequestsUnauthorisedFiles() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/unauthorised/files`
      );
      // console.log(response.data);
      this.requestsUnauthorisedFiles = response.data;
    },
    async fetchPurposes() {
      const response = await nbmlaw.get(`/cors/admin/payment/purposes`);
      // console.log(response.data);
      this.purposes = convertListOfObjectsToMap(response.data, "id");
    },
    async dismiss(request) {
      if (confirm("Are you sure?")) {
        await nbmlaw.delete(`/cors/admin/payment/requests/${request.id}`);
        // console.log(response.data);
        this.fetchRequestsUnauthorised();
      }
    },
    download(request) {
      window.open(
        `${process.env.VUE_APP_CT_URL}/cors/admin/payment/requests/${request.id}/receipt`,
        "_blank"
      );
    },
    update() {
      this.fetchRequestsAuthorised();
      this.fetchRequestsAuthorisedFiles();
      this.fetchRequestsAuthorisedAuthorises();
      this.fetchRequestsAuthorisedIssuers();
      this.fetchRequestsTaken();
      this.fetchRequestsTakenFiles();
      this.fetchRequestsTakenAuthorises();
      this.fetchRequestsTakenIssuers();
      this.fetchRequestsUnauthorised();
      this.fetchRequestsUnauthorisedFiles();
      this.lastUpdated = new Date().toISOString();
    },
  },
  created() {
    this.update();
    this.fetchPurposes();
  },
};
</script>